<template>
  <DialogHeader>
    <template #left>
      <div :class="headerButtonClasses" @click="close()">Cancel</div>
    </template>
    Copyright settings
    <template #right>
      <div
        v-if="!loading"
        class="text-green-900"
        :class="headerButtonClasses"
        @click="handleClick"
      >
        Save
      </div>
      <Spinner v-else :size="14" class="ml-auto" />
    </template>
  </DialogHeader>
  <DialogContent>
    <template v-if="!isOwner">
      <div
        v-if="isCoOwner"
        class="bg-yellow-200/50 font-secondary text-[14px] leading-[20px] w-full px-3 py-2 rounded-md flex text-yellow-700 mb-5"
      >
        <Icon :size="20" name="CoOwner" class="stroke-yellow-700 mr-3" />
        <div>
          You are a co-owner of this file. You can only view its settings and
          remove yourself as a co-owner.
        </div>
      </div>

      <div
        v-else-if="isAuthor"
        class="bg-yellow-200/50 font-secondary text-[14px] leading-[20px] w-full px-3 py-2 rounded-md flex text-yellow-700 mb-5"
      >
        <Icon :size="20" name="Author" class="stroke-yellow-700 mr-3" />
        <div>
          You are an author of this file. You can only view its settings and
          remove yourself as an author.
        </div>
      </div>
    </template>

    <!-- Inputs -->
    <div class="mb-5">
      <UiInput
        v-model="title"
        label="File title"
        :disabled="shouldDisable"
        :errorMsg="titleError"
      />
    </div>
    <div class="mb-6">
      <UiTextarea
        v-model="description"
        label="File description"
        :disabled="shouldDisable"
      />
    </div>

    <div
      class="w-full py-[17px] border-t border-b border-gray-800/20 flex justify-between"
    >
      <div class="-mt-1">
        <div class="font-semibold font-primary text-[14px] mb-1">Ownership</div>
        <div class="flex items-center">
          <div class="flex items-center -ml-0.5 mr-2.5">
            <div
              v-for="(owner, index) in owners.slice(0, 3)"
              :key="owner.id"
              class="size-[30px] border-2 border-white flex items-center justify-center rounded-full"
              :class="[
                !index && 'z-1',
                index === 1 && 'z-0',
                index && '-ml-[14px]',
              ]"
            >
              <Avatar :src="owner.avatarUrl" :size="26" />
            </div>
          </div>
          <Link @click="handleOwnersClick">
            {{ ownersLinkCopy }}
          </Link>
        </div>
      </div>
      <UiButton :disabled="shouldDisable" @click="handleOwnersClick">
        Manage
      </UiButton>
    </div>

    <div
      class="w-full py-[17px] border-b border-gray-800/20 flex justify-between"
    >
      <div class="-mt-1">
        <div class="font-semibold font-primary text-[14px] mb-1">Authors</div>
        <div class="flex items-center">
          <div v-if="authorsCount" class="flex items-center -ml-0.5 mr-2.5">
            <div
              v-for="(author, index) in authors.slice(0, 3)"
              :key="author.id"
              class="size-[30px] border-2 border-white flex items-center justify-center rounded-full"
              :class="[
                !index && 'z-1',
                index === 1 && 'z-0',
                index && '-ml-[14px]',
              ]"
            >
              <Avatar :src="author.avatarUrl" :size="26" />
            </div>
          </div>
          <div v-if="!authorsCount">0 authors</div>
          <Link v-else @click="handleAuthorsClick">
            {{ authorsLinkCopy }}
          </Link>
        </div>
      </div>
      <UiButton :disabled="shouldDisable" @click="handleAuthorsClick">
        Manage
      </UiButton>
    </div>

    <div
      class="w-full py-[17px] border-b border-gray-800/20 flex justify-between"
    >
      <div class="-mt-1">
        <div class="font-semibold font-primary text-[14px] mb-1">Invitees</div>
        <div class="flex items-center">
          <div v-if="!viewersCount">0 invites</div>
          <Link v-else @click="handleInvitesClick">
            {{ invitesLinkCopy }}
          </Link>
        </div>
      </div>
      <UiButton :disabled="shouldDisable" @click="handleInvitesClick">
        Manage
      </UiButton>
    </div>
    <div
      class="w-full py-[17px] border-b border-gray-800/20 flex justify-between"
    >
      <div class="-mt-1">
        <div class="font-semibold font-primary text-[14px] mb-1">
          Privacy controls
        </div>
        <div>
          File is <span class="font-semibold slant">{{ privateStatus }}</span>
        </div>
      </div>
      <UiButton :disabled="shouldDisable" @click="handlePrivacyClick">
        Manage
      </UiButton>
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      :loading="loading"
      primary
      @click="handleClick"
    >
      Save
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import Spinner from '@/shared/components/Spinner.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { UiButton, UiInput, Icon, UiTextarea, Avatar } from '@dacr/ui'
import { CopyrightStatus, FileRole } from '@/shared/graphql/router/graphql'
import type { File } from '@/shared/graphql/router/graphql'
import { getEntityInfo } from '@/shared/utils/fileHelpers'
import { useUpdateFileSettingsMutation } from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { updateFileInExplorerCache } from '@/features/explorer/utils/explorerCache'
import { computed, ref, watch } from 'vue'
import Link from '@/shared/components/Link.vue'
import pluralize from 'pluralize'

const { close, open } = useDialog()

const props = defineProps<{
  file: File
}>()

const { mutate: updateFileSettings, loading } = useUpdateFileSettingsMutation()

const toastStore = useToastStore()

const file = ref({ ...props.file })
const title = ref(file.value.name)
const description = ref(file.value.description || '')

const titleError = ref('')

watch(title, () => {
  titleError.value = ''
})

const headerButtonClasses = computed(() => ({
  ...(loading.value && {
    'pointer-events-none': true,
    'opacity-50': true,
  }),
}))

const isInactive = computed(
  () => file.value.copyright.status === CopyrightStatus.Inactive
)

const isOwner = computed(() => file.value.role === FileRole.Owner)
const isCoOwner = computed(() => file.value.role === FileRole.CoOwner)
const isAuthor = computed(() => file.value.role === FileRole.Author)

const shouldDisable = computed(() => loading.value || isInactive.value)

const privateStatus = computed(() =>
  file.value.settings.isPrivate ? 'private' : 'public'
)

const owners = computed(() => {
  const result: { id: string; name: string; avatarUrl: string }[] = []
  const ownerInfo = file.value.owner
  if (ownerInfo && typeof ownerInfo === 'object' && 'id' in ownerInfo) {
    result.push(getEntityInfo(ownerInfo))
  }
  if (file.value.coOwners?.edges) {
    for (const edge of file.value.coOwners.edges) {
      if (edge.node.__typename === 'EntityWithRole') {
        result.push(getEntityInfo(edge.node.entity))
      }
    }
  }
  return result
})

const authors = computed(() => {
  const result: { id: string; name: string; avatarUrl: string }[] = []
  if (file.value.authors?.edges) {
    for (const edge of file.value.authors.edges) {
      if (edge.node.__typename === 'EntityWithRole') {
        result.push(getEntityInfo(edge.node.entity))
      }
    }
  }
  return result
})

const ownersCount = computed(() => file.value.coOwners.totalCount + 1)
const authorsCount = computed(() => file.value.authors.totalCount)
const viewersCount = computed(() => file.value.viewers?.totalCount ?? 0)

const ownersLinkCopy = computed(
  () => `${ownersCount.value} ${pluralize('owner', ownersCount.value || 0)}`
)

const authorsLinkCopy = computed(
  () => `${authorsCount.value} ${pluralize('author', authorsCount.value || 0)}`
)

const invitesLinkCopy = computed(
  () =>
    `${viewersCount.value} ${pluralize('has', viewersCount.value || 0)} access`
)

const handleOwnersClick = () => {
  open(Dialogs.FileOwners, {
    props: { file: file.value },
    stack: true,
    events: {
      success(data: File) {
        file.value = data
      },
    },
  })
}

const handleAuthorsClick = () => {
  open(Dialogs.FileAuthors, {
    props: { file: file.value },
    stack: true,
    events: {
      success(data: File) {
        file.value = data
      },
    },
  })
}

const handleInvitesClick = () => {
  open(Dialogs.FileAccess, {
    props: { file: file.value },
    stack: true,
    events: {
      success(data: File) {
        file.value = data
      },
    },
  })
}

const handlePrivacyClick = () => {
  open(Dialogs.FilePrivacySettings, {
    props: { file: file.value },
    stack: true,
    events: {
      success(data: File) {
        file.value = data
      },
    },
  })
}

const handleClick = async () => {
  if (!title.value.trim()) {
    titleError.value = 'Please enter a file title'
    return
  }

  try {
    await updateFileSettings(
      {
        input: {
          id: file.value.id,
          name: title.value,
          description: description.value,
        },
      },
      {
        update() {
          updateFileInExplorerCache(file.value.id, {
            name: title.value,
            description: description.value,
          })
        },
      }
    )

    close()
  } catch (error) {
    if (error instanceof Error) {
      toastStore.showToast({
        type: ToastType.Error,
        content: 'Could not save copyright settings - please try again later',
        error: error,
      })
    }
  }
}
</script>
